import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Header from "./header/header";
import Footer from "./Footer";
import {
  FacebookShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  TwitterShareButton,
} from "next-share";
import { ClipLoader } from "react-spinners";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { baseUrl, GetToken } from "../App";
import { BlogType } from "./type";

const site = "https://academy.doyenify.com";

function BlogDetailsSlug() {
  const { blogId } = useParams<{
    blogId: string;
    slug: string;
  }>();
  const [blog, setBlog] = useState<BlogType | null>(null);
  const [ready, setReady] = useState(false);
  const { slug } = useParams<{ slug: string }>();

  const createSlug = (title: string) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-");
  };

  const fetchBlogDetails = useCallback(async () => {
    try {
      const IdToken = await GetToken();

      const response = await axios.get(`${baseUrl}/blog-posts`, {
        headers: {
          Authorization: `Bearer ${IdToken}`,
        },
      });

      const blogs = response.data || [];

      const foundBlog = blogs.find(
        (item: BlogType) => createSlug(item.Title) === slug
      );

      setBlog(foundBlog || null);
      setReady(true);
    } catch (error) {
      console.error("Error fetching blog:", error);
    }
  }, [slug]);


  useEffect(() => {
    window.scrollTo(0, 0); 
    setReady(false); 
    fetchBlogDetails();
  }, [fetchBlogDetails, blogId]);

 
  useEffect(() => {
    if (ready) {
      const scrollTimer = setTimeout(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "instant" as ScrollBehavior
        });
      }, 0);

      return () => clearTimeout(scrollTimer);
    }
  }, [ready]);

  if (!ready) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white gap-4">
        <img 
          src="../../images/doyenifyAcademy.png" 
          alt="doy academy" 
          className="h-12 object-contain"
        />
        <ClipLoader size={25} color="#047E35" />
      </div>
    );
  }

  const authorInitial = blog?.Author ? blog.Author.charAt(0).toUpperCase() : "A";

  return (
    <>
      <Header />
      
      <div className="min-h-screen bg-gray-50 font-sans pb-16">
        
        {/* 1. HERO BANNER HEADER */}
        <div className="relative w-full h-[400px] md:h-[500px] bg-gray-900 flex flex-col items-center justify-center text-center px-4 overflow-hidden">
          <img
            src={blog?.ArticleImage}
            alt="Hero Background"
            className="absolute inset-0 w-full h-full object-cover opacity-25 blur-[1px]"
          />
          
          <div className="relative z-10 max-w-5xl mx-auto flex flex-col items-center px-4">
            {/* Category Capsule Tag */}
            <span className="bg-[#047E35] backdrop-blur-md text-white text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6 border border-white/15">
              {blog?.Category || "Faith & Resilience"}
            </span>
            
           
            <h1 className="text-4xl sm:text-3xl md:text-6xl font-extrabold text-white leading-tight max-w-4xl drop-shadow-md tracking-tight">
              {blog?.Title}
            </h1>
          </div>
        </div>

       
        <div className="max-w-4xl mx-auto px-4 sm:px-6 -mt-24 relative z-25">
          <div className="bg-white rounded-3xl p-6 sm:p-10 md:p-12 shadow-xl border border-gray-100">

          
            <div className="flex flex-row justify-between items-center pb-8 border-b border-gray-100 mb-8 w-full gap-2">
              
              <div className="flex items-center">
                <div className="h-12 w-12 rounded-full bg-green-50 flex items-center justify-center text-[#047E35] font-bold text-xl shadow-sm flex-shrink-0">
                  {authorInitial}
                </div>
                <div className="ml-3.5">
                  <p className="text-lg sm:text-base font-bold text-gray-900 tracking-tight leading-none">
                    {blog?.Author || "Anonymous"}
                  </p>
                  <p className="text-sm sm:text-xs text-gray-500  mt-1.5">
                    Author
                  </p>
                </div>
              </div>

             
              <div className="text-right flex-shrink-0">
                <p className="text-sm sm:text-xs text-gray-500 ">
                  Published on
                </p>
                <p className="text-base sm:text-sm text-gray-700 mt-1">
                  {blog?.Date}
                </p>
              </div>
            </div>

           
            <div className="text-gray-700 leading-relaxed text-base md:text-lg space-y-6 overflow-hidden">
              {blog?.FullPost && (
                <div 
                  className="prose prose-green max-w-none focus:outline-none clean-post-markup" 
                  dangerouslySetInnerHTML={{ __html: blog.FullPost }} 
                />
              )}
            </div>

          
            <div className="mt-8 pt-8 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between">
              <div className="flex flex-wrap gap-2  ">
                
                <FacebookShareButton
                  url={`${site}/blog/${slug}`}
                  quote={`${blog?.Title} - ${blog?.Date}`}
                  title={`${blog?.Title} - ${blog?.Date}`}
                  hashtag="#DoyenifyAcademyBlogs"
                >
                  <div className="px-5 py-2.5 bg-[#1877F2] hover:bg-[#166FE5] text-white font-semibold text-xs rounded-lg transition-colors shadow-sm cursor-pointer">
                    Facebook
                  </div>
                </FacebookShareButton>

                <LinkedinShareButton
                  url={`${site}/blog/${slug}`}
                  title={`${blog?.Title} - ${blog?.Date}`}
                >
                  <div className="px-5 py-2.5 bg-[#0A66C2] hover:bg-[#004182] text-white font-semibold text-xs rounded-lg transition-colors shadow-sm cursor-pointer">
                    LinkedIn
                  </div>
                </LinkedinShareButton>

                <WhatsappShareButton
                  url={`${site}/blog/${slug}`}
                  title={`${blog?.Title} - ${blog?.Date}`}
                  separator=":: "
                >
                  <div className="px-5 py-2.5 bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold text-xs rounded-lg transition-colors shadow-sm cursor-pointer">
                    Whatsapp
                  </div>
                </WhatsappShareButton>

                <TwitterShareButton
                  url={`${site}/blog/${slug}`}
                  title={`${blog?.Title} - ${blog?.Date}`}
                >
                  <div className="px-5 py-2.5 bg-[#1DA1F2] hover:bg-[#1A91DA] text-white font-semibold text-xs rounded-lg transition-colors shadow-sm cursor-pointer">
                    Twitter
                  </div>
                </TwitterShareButton>

              </div>
            </div>

            
            <div className="mt-12 flex justify-center border-t border-gray-100">
              <a
                href="/blogs"
                className="text-[#047E35] hover:text-[#036128] font-semibold transition-all flex items-center gap-2 text-sm px-6 py-2.5 text-decoration-none"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="text-xs text-[#047E35]" />
                Back to all blogs
              </a>
            </div>

          </div>
        </div>

      </div>

      <Footer />
    </>
  );
}

export default BlogDetailsSlug;